<template>
  <div id="campusMapWrapper">
    <MapButtons 
      @showDrawer="showDrawer"
      :locationName="props.locationName"
      :accessUrl="props.accessUrl"
    />
    <GoogleMap
      :api-promise="props.googleApiPromise"
      ref="mapRef"
      :mapId="mapId"
      class="mapCanvas"
      :center="{ lat:55.872446, lng:-4.289041 }"
      :zoom="17"
      @zoom_changed="zoomChanged()"
      :fullscreenControl="false"
      :mapTypeControl="false"
      :streetViewControl="true"
      :zoomControl="true"
    >
      <CurrentLocationDot @newLocation="(newLocation) => userLocation = newLocation"/>
      <BuildingOutlines :map-zoom-level="mapZoomLevel"/>
      <MapFeatureMarkers :selectedFeatures="selectedFeatures" />
      <CustomControl position="RIGHT_BOTTOM" :index="2" v-if="userLocation != null">
        <button id="currentLocation" aria-label="Current location" @click="goToLocation()"><font-awesome-icon icon="location-crosshairs" /></button>
      </CustomControl>
      <AccessibleRoutes />
        
      <slot></slot>
    </GoogleMap>
    <CopyMapLink />
  </div>
  <Drawer ref="drawer_ref" :title="drawerTitle">
    <MapFeatureSelect 
      v-show="drawerContent === 'Map Features'"
      @featuresSelected="(features)=>selectedFeatures = features"
      @close="drawer_ref.hide()"/>
    <DirectionOptions
      v-show="drawerContent === 'Directions'"
      :locationName="props.locationName"
      :roomToRoom="props.roomToRoom"
      :accessUrl="props.Url"
    />
  </Drawer>
</template>

<script setup>
import {GoogleMap, CustomControl} from "vue3-google-map"
import CurrentLocationDot from "@/components/map/CurrentLocationDot.vue"
import BuildingOutlines from "@/components/map/BuildingOutlines.vue"
import {ref, watch, onMounted, defineProps} from "vue"
import MapFeatureMarkers from "./MapFeatureMarkers.vue"
import MapFeatureSelect from "./MapFeatureSelection.vue"
import Drawer from "@/components/Drawer.vue"
import DirectionOptions from "./DirectionOptions.vue"
import MapButtons from './MapButtons.vue'
import CopyMapLink from "./CopyMapLink.vue"
import AccessibleRoutes from "@/components/map/AccessibleRoutes.vue";

//Drawer
const drawer_ref = ref(null)
const drawerTitle = ref('')
const drawerContent = ref('')


const props = defineProps({
  googleApiPromise: {
  type: Promise
  },
  panTarget: {
    type: Object
  },
  locationName: {
    type: String
  },
  roomToRoom: {
    type: Boolean,
    default: false
  },
  accessUrl: {
    type: String,
    default: ''
  }
})

const mapId = "ee99e7bb2f09a3cf4a8bf403"; // linked to bella.hodgson@glasgow.ac.uk's personal google account
const mapRef = ref(null)
const mapZoomLevel = ref(0);
const userLocation = ref(null)

//Map Features
const selectedFeatures = ref([])

/**
 * Update zoom level with value returned from map
 * 
 * @returns {void}
 */
function zoomChanged() {
  mapZoomLevel.value = mapRef.value.map.getZoom();
}

// pans to new target
watch(() => [mapRef.value?.ready, props.panTarget], () => {
  if (!mapRef.value?.ready || !props.panTarget) return;
  mapRef.value.map.panTo(props.panTarget);
})

/**
 * Update drawer content flag and show drawer
 * 
 * @param {string} content - The content to show in the drawer 
 * @returns {void}
 */
function showDrawer(content){
  if(content === 'Map Features' || content === 'Directions') {
    drawerContent.value = content
    drawerTitle.value = content
    drawer_ref.value.show()
  } else {
    drawerContent.value = ''
    console.log("no content for drawer")
  }
}

/**
 * Pan the map to the user's current location
 * 
 * @returns {void}
 */
function goToLocation() {
  mapRef.value.map.panTo(userLocation.value);
}
</script>

<style>
#campusMapWrapper {
    display: flex;
    flex-direction: column;
    /* Fallback if svh not supported */
    height: calc(100vh - 133px - env(safe-area-inset-top, 0) - env(safe-area-inset-bottom, 0));
    
}
/* Use svh if supported */
@supports(height: 100svh) {
  #campusMapWrapper {
    /* svh to accommodate mobile browser UI */
    height: calc(100svh - 133px - env(safe-area-inset-top, 0) - env(safe-area-inset-bottom, 0));
  }
}

.mapCanvas {
  flex-grow: 1;
}

#currentLocation {
    background-color :#fff;
    border :2px solid #fff;
    border-radius: 50%;
    box-shadow :0 2px 6px rgba(0,0,0,.3);
    color :rgb(25,25,25);
    cursor :pointer;
    font-family :Roboto,Arial,sans-serif;
    font-size :16px;
    line-height :38px;
    padding :0 5px;
    text-align :center;
    color: var(--uog-dark-blue);
    height: 40px;
    width: 40px;
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 0px;
    margin-right: 10px;
    margin-bottom: 10px;
}
</style>
